(function () {
  'use strict';

  if (window.UnifiedDashboardAPI) {
    return;
  }

  const VERSION = '1.0.0';
  const DEFAULT_LIMIT = 50;
  const MAX_LIMIT = 200;
  const REDACTED_VALUE = '[REDACTED]';

  const ENDPOINTS = [
    'overview',
    'modules',
    'config',
    'context',
    'health',
    'bus',
    'errors',
    'journey',
    'performance',
    'providers',
    'rules',
    'schema',
    'tracking',
    'snapshot'
  ];

  const MODULES = [
    {
      id: 'config',
      global: 'UnifiedTrackingConfig'
    },
    {
      id: 'bus',
      global: 'UnifiedBus'
    },
    {
      id: 'debug',
      global: 'UnifiedDebug'
    },
    {
      id: 'context',
      global: 'UnifiedContext'
    },
    {
      id: 'errors',
      global: 'UnifiedErrors'
    },
    {
      id: 'journey',
      global: 'UnifiedJourney'
    },
    {
      id: 'performance',
      global: 'UnifiedPerformance'
    },
    {
      id: 'schema',
      global: 'UnifiedSchema'
    },
    {
      id: 'normalizer',
      global: 'UnifiedNormalizer'
    },
    {
      id: 'validator',
      global: 'UnifiedValidator'
    },
    {
      id: 'rules_engine',
      global: 'UnifiedRulesEngine'
    },
    {
      id: 'provider_manager',
      global: 'UnifiedProviderManager'
    },
    {
      id: 'tracking',
      global: 'UnifiedTracking'
    },
    {
      id: 'detection',
      global: 'UnifiedDetection'
    },
    {
      id: 'health',
      global: 'UnifiedHealth'
    },
    {
      id: 'dashboard_api',
      global: 'UnifiedDashboardAPI'
    }
  ];

  const SENSITIVE_KEYS = [
    'email',
    'email_address',
    'customer_email',
    'phone',
    'telephone',
    'phone_number',
    'customer_phone',
    'mobile',
    'first_name',
    'last_name',
    'full_name',
    'customer_name',
    'contact_name',
    'address',
    'street',
    'postal_code',
    'zip_code'
  ];

  const EMAIL_PATTERN =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function now() {
    return new Date().toISOString();
  }

  function isObject(value) {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function normalizeOptions(options) {
    return isObject(options)
      ? options
      : {};
  }

  function normalizeLimit(value, fallback) {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return fallback || DEFAULT_LIMIT;
    }

    return Math.min(
      MAX_LIMIT,
      Math.max(0, Math.floor(number))
    );
  }

  function normalizeOrder(value) {
    return value === 'asc'
      ? 'asc'
      : 'desc';
  }

  function safeClone(value) {
    const seen = new WeakSet();

    function visit(source) {
      if (
        source === null ||
        typeof source === 'undefined'
      ) {
        return source;
      }

      if (
        typeof source === 'string' ||
        typeof source === 'number' ||
        typeof source === 'boolean'
      ) {
        return source;
      }

      if (typeof source === 'bigint') {
        return source.toString();
      }

      if (typeof source === 'function') {
        return '[Function]';
      }

      if (source instanceof Date) {
        return source.toISOString();
      }

      if (source instanceof RegExp) {
        return source.toString();
      }

      if (typeof source !== 'object') {
        return String(source);
      }

      if (seen.has(source)) {
        return '[Circular]';
      }

      seen.add(source);

      if (Array.isArray(source)) {
        const arrayResult = source.map(
          function (item) {
            return visit(item);
          }
        );

        seen.delete(source);
        return arrayResult;
      }

      const objectResult = {};

      Object.keys(source)
        .forEach(function (key) {
          try {
            objectResult[key] =
              visit(source[key]);
          } catch (error) {
            objectResult[key] =
              '[Unavailable]';
          }
        });

      seen.delete(source);
      return objectResult;
    }

    return visit(value);
  }

  function isSensitiveKey(key) {
    const normalized = String(
      key || ''
    ).toLowerCase();

    return SENSITIVE_KEYS.some(
      function (sensitiveKey) {
        return (
          normalized === sensitiveKey ||
          normalized.endsWith(
            '_' + sensitiveKey
          )
        );
      }
    );
  }

  function redact(value, options) {
    const settings = normalizeOptions(options);

    if (settings.include_sensitive === true) {
      return safeClone(value);
    }

    const seen = new WeakSet();

    function visit(source, keyName) {
      if (isSensitiveKey(keyName)) {
        return REDACTED_VALUE;
      }

      if (
        source === null ||
        typeof source === 'undefined'
      ) {
        return source;
      }

      if (typeof source === 'string') {
        const trimmed = source.trim();

        if (EMAIL_PATTERN.test(trimmed)) {
          return REDACTED_VALUE;
        }

        return source;
      }

      if (
        typeof source === 'number' ||
        typeof source === 'boolean'
      ) {
        return source;
      }

      if (typeof source === 'bigint') {
        return source.toString();
      }

      if (typeof source === 'function') {
        return '[Function]';
      }

      if (source instanceof Date) {
        return source.toISOString();
      }

      if (source instanceof RegExp) {
        return source.toString();
      }

      if (typeof source !== 'object') {
        return String(source);
      }

      if (seen.has(source)) {
        return '[Circular]';
      }

      seen.add(source);

      if (Array.isArray(source)) {
        const arrayResult = source.map(
          function (item) {
            return visit(item, '');
          }
        );

        seen.delete(source);
        return arrayResult;
      }

      const objectResult = {};

      Object.keys(source)
        .forEach(function (key) {
          try {
            objectResult[key] =
              visit(source[key], key);
          } catch (error) {
            objectResult[key] =
              '[Unavailable]';
          }
        });

      seen.delete(source);
      return objectResult;
    }

    return visit(value, '');
  }

  function getGlobal(name) {
    try {
      return window[name] || null;
    } catch (error) {
      return null;
    }
  }

  function safeCall(
    target,
    methodName,
    args,
    fallback
  ) {
    if (
      !target ||
      typeof target[methodName] !==
        'function'
    ) {
      return fallback;
    }

    try {
      return target[methodName].apply(
        target,
        Array.isArray(args)
          ? args
          : []
      );
    } catch (error) {
      return fallback;
    }
  }

  function getCapabilities(target) {
    if (!target) {
      return [];
    }

    return Object.keys(target)
      .filter(function (key) {
        return (
          typeof target[key] ===
          'function'
        );
      })
      .sort();
  }

  function getModules(options) {
    const settings = normalizeOptions(options);

    const modules = MODULES.map(
      function (definition) {
        const target = getGlobal(
          definition.global
        );

        return {
          id: definition.id,
          global: definition.global,
          available: Boolean(target),
          version:
            target && target.version
              ? String(target.version)
              : '',
          capabilities:
            settings.include_capabilities ===
              false
              ? []
              : getCapabilities(target)
        };
      }
    );

    return {
      generated_at: now(),
      total: modules.length,
      available: modules.filter(
        function (module) {
          return module.available;
        }
      ).length,
      missing: modules.filter(
        function (module) {
          return !module.available;
        }
      ).map(function (module) {
        return module.id;
      }),
      modules: modules
    };
  }

  function getConfig(options) {
    const config = getGlobal(
      'UnifiedTrackingConfig'
    );

    return {
      available: Boolean(config),
      generated_at: now(),
      data: redact(
        config || {},
        options
      )
    };
  }

  function getContext(options) {
    const contextModule = getGlobal(
      'UnifiedContext'
    );

    const context = safeCall(
      contextModule,
      'getContext',
      [],
      {
        page_location:
          window.location &&
          window.location.href
            ? window.location.href
            : '',
        page_path:
          window.location &&
          window.location.pathname
            ? window.location.pathname
            : '',
        page_title:
          window.document &&
          window.document.title
            ? window.document.title
            : ''
      }
    );

    return {
      available: Boolean(contextModule),
      generated_at: now(),
      data: redact(context, options)
    };
  }

  function getHealth(options) {
    const health = getGlobal(
      'UnifiedHealth'
    );

    const status = safeCall(
      health,
      'getStatus',
      [],
      null
    );

    return {
      available: Boolean(health),
      generated_at: now(),
      healthy:
        status &&
        typeof status.healthy ===
          'boolean'
          ? status.healthy
          : null,
      data: redact(
        status,
        options
      )
    };
  }

  function filterByTimestamp(
    entries,
    after,
    before
  ) {
    if (!after && !before) {
      return entries;
    }

    const afterTime = after
      ? Date.parse(after)
      : null;

    const beforeTime = before
      ? Date.parse(before)
      : null;

    return entries.filter(
      function (entry) {
        const timestamp = Date.parse(
          entry && entry.timestamp
            ? entry.timestamp
            : ''
        );

        if (!Number.isFinite(timestamp)) {
          return false;
        }

        if (
          Number.isFinite(afterTime) &&
          timestamp < afterTime
        ) {
          return false;
        }

        if (
          Number.isFinite(beforeTime) &&
          timestamp > beforeTime
        ) {
          return false;
        }

        return true;
      }
    );
  }

  function applyOrderAndLimit(
    entries,
    options
  ) {
    const settings = normalizeOptions(options);
    const order = normalizeOrder(
      settings.order
    );
    const limit = normalizeLimit(
      settings.limit,
      DEFAULT_LIMIT
    );

    const ordered = entries.slice();

    if (order === 'desc') {
      ordered.reverse();
    }

    return {
      order: order,
      limit: limit,
      entries:
        limit === 0
          ? []
          : ordered.slice(0, limit)
    };
  }

  function getBus(options) {
    const settings = normalizeOptions(options);
    const bus = getGlobal('UnifiedBus');
    const source = safeCall(
      bus,
      'getHistory',
      [],
      []
    );

    let entries = Array.isArray(source)
      ? source.slice()
      : [];

    const types = Array.isArray(
      settings.types
    )
      ? settings.types.map(String)
      : settings.type
        ? [String(settings.type)]
        : [];

    if (types.length) {
      entries = entries.filter(
        function (entry) {
          return (
            entry &&
            types.indexOf(
              String(entry.type)
            ) !== -1
          );
        }
      );
    }

    if (settings.event) {
      entries = entries.filter(
        function (entry) {
          const data = entry && entry.data
            ? entry.data
            : {};

          return (
            data.event ===
            settings.event
          );
        }
      );
    }

    entries = filterByTimestamp(
      entries,
      settings.after,
      settings.before
    );

    const selection =
      applyOrderAndLimit(
        entries,
        settings
      );

    const selectedEntries =
      settings.include_data === false
        ? selection.entries.map(
            function (entry) {
              return {
                id: entry.id,
                type: entry.type,
                timestamp:
                  entry.timestamp
              };
            }
          )
        : selection.entries;

    return {
      available: Boolean(bus),
      generated_at: now(),
      total_history:
        Array.isArray(source)
          ? source.length
          : 0,
      matched: entries.length,
      returned:
        selectedEntries.length,
      filters: {
        types: types,
        event:
          settings.event || '',
        after:
          settings.after || '',
        before:
          settings.before || '',
        order: selection.order,
        limit: selection.limit
      },
      stats: redact(
        safeCall(
          bus,
          'getStats',
          [],
          null
        ),
        settings
      ),
      entries: redact(
        selectedEntries,
        settings
      )
    };
  }

  function getErrors(options) {
    const settings = normalizeOptions(options);
    const errors = getGlobal(
      'UnifiedErrors'
    );
    const source = safeCall(
      errors,
      'getAll',
      [],
      []
    );

    let entries = Array.isArray(source)
      ? source.slice()
      : [];

    if (settings.severity) {
      entries = entries.filter(
        function (entry) {
          return (
            entry &&
            entry.severity ===
              settings.severity
          );
        }
      );
    }

    if (settings.code) {
      entries = entries.filter(
        function (entry) {
          return (
            entry &&
            entry.code ===
              settings.code
          );
        }
      );
    }

    entries = filterByTimestamp(
      entries,
      settings.after,
      settings.before
    );

    const selection =
      applyOrderAndLimit(
        entries,
        settings
      );

    return {
      available: Boolean(errors),
      generated_at: now(),
      total_entries:
        Array.isArray(source)
          ? source.length
          : 0,
      matched: entries.length,
      returned:
        selection.entries.length,
      summary: redact(
        safeCall(
          errors,
          'getSummary',
          [],
          null
        ),
        settings
      ),
      entries: redact(
        selection.entries,
        settings
      )
    };
  }

  function getJourney(options) {
    const settings = normalizeOptions(options);
    const journey = getGlobal(
      'UnifiedJourney'
    );
    const source = safeCall(
      journey,
      'getJourney',
      [],
      []
    );

    let steps = Array.isArray(source)
      ? source.slice()
      : [];

    if (settings.event) {
      steps = steps.filter(
        function (step) {
          return (
            step &&
            step.event ===
              settings.event
          );
        }
      );
    }

    if (settings.page_path) {
      steps = steps.filter(
        function (step) {
          return (
            step &&
            step.page_path ===
              settings.page_path
          );
        }
      );
    }

    steps = filterByTimestamp(
      steps,
      settings.after,
      settings.before
    );

    const selection =
      applyOrderAndLimit(
        steps,
        settings
      );

    return {
      available: Boolean(journey),
      generated_at: now(),
      total_steps:
        Array.isArray(source)
          ? source.length
          : 0,
      matched: steps.length,
      returned:
        selection.entries.length,
      summary: redact(
        safeCall(
          journey,
          'getSummary',
          [],
          null
        ),
        settings
      ),
      conversion_path:
        settings.include_conversion_path ===
          false
          ? null
          : redact(
              safeCall(
                journey,
                'getConversionPath',
                [],
                null
              ),
              settings
            ),
      steps: redact(
        selection.entries,
        settings
      )
    };
  }

  function getPerformance(options) {
    const settings = normalizeOptions(options);
    const performanceModule = getGlobal(
      'UnifiedPerformance'
    );
    const source = safeCall(
      performanceModule,
      'getMeasurements',
      [],
      []
    );

    let measurements = Array.isArray(source)
      ? source.slice()
      : [];

    if (settings.label) {
      measurements = measurements.filter(
        function (measurement) {
          return (
            measurement &&
            measurement.label ===
              settings.label
          );
        }
      );
    }

    const selection =
      applyOrderAndLimit(
        measurements,
        settings
      );

    return {
      available:
        Boolean(performanceModule),
      generated_at: now(),
      total_measurements:
        Array.isArray(source)
          ? source.length
          : 0,
      matched: measurements.length,
      returned:
        selection.entries.length,
      summary: redact(
        safeCall(
          performanceModule,
          'getSummary',
          [],
          null
        ),
        settings
      ),
      measurements: redact(
        selection.entries,
        settings
      )
    };
  }

  function getProviders(options) {
    const settings = normalizeOptions(options);
    const manager = getGlobal(
      'UnifiedProviderManager'
    );

    const providers = safeCall(
      manager,
      'getProviders',
      [],
      []
    );

    const sourceHistory = safeCall(
      manager,
      'getDispatchHistory',
      [],
      []
    );

    let history = Array.isArray(
      sourceHistory
    )
      ? sourceHistory.slice()
      : [];

    if (settings.event) {
      history = history.filter(
        function (entry) {
          return (
            entry &&
            entry.event ===
              settings.event
          );
        }
      );
    }

    if (
      typeof settings.delivered ===
      'boolean'
    ) {
      history = history.filter(
        function (entry) {
          return (
            entry &&
            entry.delivered ===
              settings.delivered
          );
        }
      );
    }

    const selection =
      applyOrderAndLimit(
        history,
        settings
      );

    return {
      available: Boolean(manager),
      generated_at: now(),
      providers: redact(
        providers,
        settings
      ),
      default_routes: redact(
        safeCall(
          manager,
          'getDefaultRoutes',
          [],
          []
        ),
        settings
      ),
      stats: redact(
        safeCall(
          manager,
          'getStats',
          [],
          null
        ),
        settings
      ),
      dispatch_history: {
        total:
          Array.isArray(sourceHistory)
            ? sourceHistory.length
            : 0,
        matched: history.length,
        returned:
          selection.entries.length,
        entries: redact(
          selection.entries,
          settings
        )
      }
    };
  }

  function getRules(options) {
    const settings = normalizeOptions(options);
    const rulesEngine = getGlobal(
      'UnifiedRulesEngine'
    );

    return {
      available: Boolean(rulesEngine),
      generated_at: now(),
      version:
        rulesEngine &&
        rulesEngine.version
          ? String(
              rulesEngine.version
            )
          : '',
      configuration: redact(
        safeCall(
          rulesEngine,
          'getConfiguration',
          [],
          null
        ),
        settings
      ),
      configured_rules: redact(
        safeCall(
          rulesEngine,
          'getConfiguredRules',
          [],
          []
        ),
        settings
      ),
      supported_actions: redact(
        safeCall(
          rulesEngine,
          'getSupportedActions',
          [],
          []
        ),
        settings
      ),
      supported_operators: redact(
        safeCall(
          rulesEngine,
          'getSupportedOperators',
          [],
          []
        ),
        settings
      )
    };
  }

  function getSchema(options) {
    const settings = normalizeOptions(options);
    const schema = getGlobal(
      'UnifiedSchema'
    );

    const events = schema &&
      schema.events &&
      typeof schema.events ===
        'object'
      ? Object.keys(schema.events)
      : [];

    return {
      available: Boolean(schema),
      generated_at: now(),
      version:
        schema && schema.version
          ? String(schema.version)
          : '',
      event_count: events.length,
      events: events,
      core_fields: redact(
        schema && schema.coreFields
          ? schema.coreFields
          : {},
        settings
      ),
      aliases: redact(
        schema && schema.aliases
          ? schema.aliases
          : {},
        settings
      ),
      normalizers: redact(
        schema && schema.normalizers
          ? schema.normalizers
          : {},
        settings
      )
    };
  }

  function getTracking(options) {
    const settings = normalizeOptions(options);
    const tracking = getGlobal(
      'UnifiedTracking'
    );
    const config = getGlobal(
      'UnifiedTrackingConfig'
    );

    const consent = safeCall(
      tracking,
      'hasAnalyticsConsent',
      [],
      null
    );

    return {
      available: Boolean(tracking),
      generated_at: now(),
      framework_version:
        config && config.version
          ? String(config.version)
          : '',
      debug:
        config &&
        typeof config.debug ===
          'boolean'
          ? config.debug
          : null,
      consent_required:
        config &&
        typeof config.consentRequired ===
          'boolean'
          ? config.consentRequired
          : null,
      analytics_consent:
        typeof consent === 'boolean'
          ? consent
          : null,
      provider_manager:
        Boolean(
          safeCall(
            tracking,
            'getProviderManager',
            [],
            null
          )
        ),
      capabilities:
        settings.include_capabilities ===
          false
          ? []
          : getCapabilities(tracking)
    };
  }

  function getOverview(options) {
    const settings = normalizeOptions(options);
    const config = getGlobal(
      'UnifiedTrackingConfig'
    );
    const health = getHealth(settings);
    const modules = getModules({
      include_capabilities: false
    });
    const bus = getBus({
      limit: 0,
      include_data: false,
      include_sensitive:
        settings.include_sensitive
    });
    const errors = getErrors({
      limit: 0,
      include_sensitive:
        settings.include_sensitive
    });
    const journey = getJourney({
      limit: 0,
      include_conversion_path: false,
      include_sensitive:
        settings.include_sensitive
    });
    const performanceData =
      getPerformance({
        limit: 0,
        include_sensitive:
          settings.include_sensitive
      });
    const providers = getProviders({
      limit: 0,
      include_sensitive:
        settings.include_sensitive
    });
    const tracking = getTracking({
      include_capabilities: false
    });

    return {
      generated_at: now(),
      api_version: VERSION,
      framework_version:
        config && config.version
          ? String(config.version)
          : '',
      debug:
        config &&
        typeof config.debug ===
          'boolean'
          ? config.debug
          : null,
      healthy: health.healthy,
      health_score:
        health.data &&
        health.data.score
          ? health.data.score
          : null,
      analytics_consent:
        tracking.analytics_consent,
      modules: {
        total: modules.total,
        available:
          modules.available,
        missing: modules.missing
      },
      bus_events:
        bus.total_history,
      errors:
        errors.summary,
      journey_steps:
        journey.total_steps,
      performance_measurements:
        performanceData
          .total_measurements,
      providers:
        providers.stats,
      page: redact(
        {
          location:
            window.location &&
            window.location.href
              ? window.location.href
              : '',
          path:
            window.location &&
            window.location.pathname
              ? window.location.pathname
              : '',
          title:
            window.document &&
            window.document.title
              ? window.document.title
              : ''
        },
        settings
      )
    };
  }

  function normalizeIncludes(value) {
    const defaults = [
      'overview',
      'modules',
      'health',
      'config',
      'context',
      'bus',
      'errors',
      'journey',
      'performance',
      'providers',
      'rules',
      'schema',
      'tracking'
    ];

    if (!Array.isArray(value)) {
      return defaults;
    }

    return value.filter(
      function (item, index, list) {
        return (
          defaults.indexOf(item) !== -1 &&
          list.indexOf(item) === index
        );
      }
    );
  }

  function getSnapshot(options) {
    const settings = normalizeOptions(options);
    const include = normalizeIncludes(
      settings.include
    );
    const limit = normalizeLimit(
      settings.limit,
      DEFAULT_LIMIT
    );
    const common = {
      limit: limit,
      order:
        settings.order || 'desc',
      include_sensitive:
        settings.include_sensitive ===
        true
    };

    const snapshot = {
      generated_at: now(),
      api_version: VERSION,
      include: include.slice(),
      privacy: {
        sensitive_data_included:
          settings.include_sensitive ===
          true,
        redacted_value:
          REDACTED_VALUE
      }
    };

    include.forEach(function (section) {
      if (section === 'overview') {
        snapshot.overview =
          getOverview(common);
      }

      if (section === 'modules') {
        snapshot.modules =
          getModules({
            include_capabilities:
              settings
                .include_capabilities !==
              false
          });
      }

      if (section === 'health') {
        snapshot.health =
          getHealth(common);
      }

      if (section === 'config') {
        snapshot.config =
          getConfig(common);
      }

      if (section === 'context') {
        snapshot.context =
          getContext(common);
      }

      if (section === 'bus') {
        snapshot.bus = getBus(
          Object.assign({}, common, {
            types:
              settings.bus_types,
            event:
              settings.event,
            include_data:
              settings.include_bus_data !==
              false
          })
        );
      }

      if (section === 'errors') {
        snapshot.errors = getErrors(
          Object.assign({}, common, {
            severity:
              settings.severity,
            code: settings.code
          })
        );
      }

      if (section === 'journey') {
        snapshot.journey = getJourney(
          Object.assign({}, common, {
            event:
              settings.event,
            page_path:
              settings.page_path,
            include_conversion_path:
              settings
                .include_conversion_path !==
              false
          })
        );
      }

      if (section === 'performance') {
        snapshot.performance =
          getPerformance(
            Object.assign({}, common, {
              label:
                settings.label
            })
          );
      }

      if (section === 'providers') {
        snapshot.providers =
          getProviders(
            Object.assign({}, common, {
              event:
                settings.event,
              delivered:
                settings.delivered
            })
          );
      }

      if (section === 'rules') {
        snapshot.rules =
          getRules(common);
      }

      if (section === 'schema') {
        snapshot.schema =
          getSchema(common);
      }

      if (section === 'tracking') {
        snapshot.tracking =
          getTracking({
            include_capabilities:
              settings
                .include_capabilities !==
              false
          });
      }
    });

    return snapshot;
  }

  function request(endpoint, options) {
    const name = String(
      endpoint || ''
    ).toLowerCase();

    const handlers = {
      overview: getOverview,
      modules: getModules,
      config: getConfig,
      context: getContext,
      health: getHealth,
      bus: getBus,
      errors: getErrors,
      journey: getJourney,
      performance: getPerformance,
      providers: getProviders,
      rules: getRules,
      schema: getSchema,
      tracking: getTracking,
      snapshot: getSnapshot
    };

    if (!handlers[name]) {
      return {
        success: false,
        endpoint: name,
        generated_at: now(),
        error: {
          code: 'dashboard_endpoint_unknown',
          message:
            'Unknown Dashboard API endpoint.'
        },
        supported_endpoints:
          ENDPOINTS.slice()
      };
    }

    try {
      return {
        success: true,
        endpoint: name,
        generated_at: now(),
        data: handlers[name](options)
      };
    } catch (error) {
      return {
        success: false,
        endpoint: name,
        generated_at: now(),
        error: {
          code: 'dashboard_endpoint_failed',
          message:
            error && error.message
              ? error.message
              : 'Dashboard endpoint failed.'
        }
      };
    }
  }

  function toJSON(options, spacing) {
    const indent = Number.isFinite(
      Number(spacing)
    )
      ? Math.min(
          10,
          Math.max(
            0,
            Math.floor(Number(spacing))
          )
        )
      : 2;

    return JSON.stringify(
      getSnapshot(options),
      null,
      indent
    );
  }

  function printOverview(options) {
    const overview = getOverview(options);

    console.info(
      '[Unified Dashboard API] Overview',
      overview
    );

    return overview;
  }

  window.UnifiedDashboardAPI = {
    version: VERSION,

    getEndpoints: function () {
      return ENDPOINTS.slice();
    },

    getModules: getModules,
    getOverview: getOverview,
    getConfig: getConfig,
    getContext: getContext,
    getHealth: getHealth,
    getBus: getBus,
    getErrors: getErrors,
    getJourney: getJourney,
    getPerformance: getPerformance,
    getProviders: getProviders,
    getRules: getRules,
    getSchema: getSchema,
    getTracking: getTracking,
    getSnapshot: getSnapshot,
    request: request,
    toJSON: toJSON,
    redact: redact,
    printOverview: printOverview,

    limits: {
      default: DEFAULT_LIMIT,
      maximum: MAX_LIMIT
    },

    privacy: {
      redacted_value:
        REDACTED_VALUE,
      sensitive_keys:
        SENSITIVE_KEYS.slice(),
      note:
        'Redaction is a diagnostic safeguard, not a security boundary.'
    }
  };

  if (
    window.UnifiedTrackingConfig &&
    window.UnifiedTrackingConfig.debug
  ) {
    console.info(
      '[Unified Dashboard API] Dashboard API initialized',
      {
        version: VERSION,
        endpoints:
          ENDPOINTS.length,
        defaultLimit:
          DEFAULT_LIMIT,
        maximumLimit:
          MAX_LIMIT,
        redaction:
          'enabled_by_default'
      }
    );
  }
})();
